import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchAskableStaff, MY_ASKS_KEY, raiseAsk } from '../api/asks'
import { fetchContent, fill, text } from '../api/content'
import { addDays, formatDayLabel, formatFullDate, today } from '../api/calendar'
import { fetchStudioDates, upcoming } from '../api/studioSetup'
import Modal from './Modal'

/**
 * Put something on a colleague's list (D-549).
 *
 * **The ninth kind, and the one with nothing behind it**: no booking, no
 * class, no dancer — a title, a note and a day. It is raised through the same
 * door every kind uses, so this dialog knows only what a task is made of; the
 * server decides who may assign one and refuses a person who cannot sign in.
 *
 * The list is the one the door accepts — active staff who can sign in — so
 * nothing offered here is refused on submit.
 */
export default function AssignTaskDialog({
  onClose,
  suggested,
  onRaised,
}: {
  onClose: () => void
  /** The task's id once it exists (D-630), so a conversation can pin it. Before `onClose`. */
  onRaised?: (askId: string) => void
  /**
   * A title and a note to open with, where the caller already knows them
   * (D-568).
   *
   * <b>Suggested rather than fixed</b>: both fields stay editable, because
   * the sentence a screen can compose is a starting point and the person
   * assigning the job knows what she actually wants asked. Absent for the
   * everyday task, which is this dialog exactly as it was.
   *
   * <b>Pre-filling this rather than building a second flow</b> is the whole
   * point (A-17). A conflict handed to a teacher is a task: it lands on
   * `/asks`, it is reminded about, it carries its answer, and it needed no
   * new kind to do any of that.
   */
  suggested?: { title: string; note: string; dueOn?: string }
}) {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })
  const staff = useQuery({ queryKey: ['asks', 'staff'], queryFn: fetchAskableStaff })
  const queryClient = useQueryClient()

  const [who, setWho] = useState('')
  const [title, setTitle] = useState(suggested?.title ?? '')
  const [note, setNote] = useState(suggested?.note ?? '')
  // Seven days out by default (D-554, slice 2): a task with no day is one
  // nobody is ever reminded of, and a week is when a thing not done becomes
  // a thing overdue. Still hers to change or clear.
  const [dueOn, setDueOn] = useState(suggested?.dueOn ?? addDays(today(), 7))
  const [nudges, setNudges] = useState(true)

  /*
   * Hanging it on one of the studio's dates (D-559).
   *
   * <b>Behind a control, and that was the owner's call.</b> Everything below
   * is invisible until she asks for it, so the everyday task - "remind Ashley
   * to bring the speaker" - is the dialog it has always been, with no row of
   * competition dates on it. The list is only fetched when she opens it.
   */
  const [hanging, setHanging] = useState(false)
  const [anchorId, setAnchorId] = useState<string | null>(null)
  const [daysBefore, setDaysBefore] = useState(0)

  const dates = useQuery({
    queryKey: ['studio-dates'],
    queryFn: fetchStudioDates,
    enabled: hanging,
  })

  const choices = upcoming(dates.data ?? [])
  const anchor = choices.find((d) => d.studioDateId === anchorId) ?? null

  // The date the anchor works out to, which is what the field shows while one
  // is picked. The server does this again on save and its answer is the one
  // that counts - the list in this browser may be minutes old.
  const anchoredOn = anchor ? addDays(anchor.on, -daysBefore) : null

  /*
   * "18 Oct" on a chip, not "Sunday, October 18, 2026". Spelled out, one chip
   * fills a row on a phone and six dates make a wall - which is the layer the
   * control was put behind a link to avoid. The full date is right below, in
   * the field and in the sentence.
   */
  const shortDate = (iso: string) => {
    const { day, month } = formatDayLabel(iso)

    // The year only when it is not this one. Two Regionals thirteen months
    // apart are both upcoming, and "10 Oct" twice is the one case where the
    // date beside the name stops telling them apart - which is the whole
    // mitigation for two rows sharing a name (D-555).
    const year = iso.slice(0, 4)
    return year === today().slice(0, 4) ? `${day} ${month}` : `${day} ${month} ${year}`
  }

  const clearAnchor = () => {
    // Leaves the worked-out date in the field: it is what she has been looking
    // at, and reverting to the seven-day prefill would silently discard it.
    if (anchoredOn) setDueOn(anchoredOn)
    setAnchorId(null)
  }

  const assign = useMutation({
    mutationFn: () =>
      raiseAsk({
        kind: 'task',
        to: { type: 'Staff', id: who },
        // One or the other, never both: the server refuses a raise carrying
        // a typed date and an anchor, rather than quietly preferring one.
        closesOn: anchor ? null : dueOn || null,
        content: {
          title: title.trim(),
          note: note.trim() || null,
          // Absent rather than null when nothing is hung on it, so an ordinary
          // task's raise is byte-for-byte the one it has always been.
          ...(anchor ? { anchor: { dateId: anchor.studioDateId, daysBefore } } : {}),
        },
        nudges,
      }),
    onSuccess: (askId) => {
      queryClient.invalidateQueries({ queryKey: [...MY_ASKS_KEY] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      onRaised?.(askId)
      onClose()
    },
  })

  const ready = who !== '' && title.trim().length > 0 && !assign.isPending

  return (
    <Modal
      onDismiss={onClose}
      labelledBy="assign-task-heading"
      panelClassName="relative w-full max-w-[26rem] rounded-2xl border border-line bg-surface p-5 shadow-2xl"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (ready) assign.mutate()
        }}
        className="flex flex-col gap-3"
      >
        <h2 id="assign-task-heading" className="font-display text-lg">
          {text(content, 'asks.task.heading', 'A task for somebody')}
        </h2>

        <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-ink-2">
          {text(content, 'asks.task.who', 'Who')}
          <select
            value={who}
            onChange={(e) => setWho(e.target.value)}
            className="rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13px] font-normal text-ink"
          >
            <option value="">{text(content, 'asks.task.choose', 'Choose somebody')}</option>
            {(staff.data ?? []).map((s) => (
              <option key={s.staffId} value={s.staffId}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-ink-2">
          {text(content, 'asks.task.title', 'What')}
          <input
            value={title}
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
            className="rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13px] font-normal text-ink"
          />
        </label>

        <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-ink-2">
          {text(content, 'asks.task.note', 'Details')}
          <textarea
            value={note}
            maxLength={1000}
            rows={3}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13px] font-normal text-ink"
          />
        </label>

        <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-ink-2">
          {text(content, 'asks.task.due', 'By when')}
          <input
            type="date"
            value={anchoredOn ?? dueOn}
            disabled={anchor !== null}
            onChange={(e) => setDueOn(e.target.value)}
            className="rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13px] font-normal text-ink disabled:opacity-60"
          />
        </label>

        {!hanging && (
          <button
            type="button"
            onClick={() => setHanging(true)}
            className="self-start text-[12.5px] font-semibold text-accent-2 hover:underline"
          >
            {text(content, 'asks.task.hangOn', 'Hang it on one of the studio\'s dates')}
          </button>
        )}

        {hanging && (
          <div className="flex flex-col gap-2 rounded-lg border border-line-2 bg-surface-2 px-3 py-2.5">
            {dates.isPending && (
              <p className="text-[12.5px] text-ink-3">
                {text(content, 'asks.task.datesLoading', 'Loading the studio\'s dates...')}
              </p>
            )}

            {dates.isError && (
              <p role="alert" className="text-[12.5px] text-crit">
                {text(content, 'asks.task.datesFailed', 'Could not load the studio\'s dates. Type a date instead.')}
              </p>
            )}

            {!dates.isPending && !dates.isError && choices.length === 0 && (
              <p className="text-[12.5px] text-ink-3">
                {text(content, 'asks.task.noDates', 'The studio has no dates coming up. Tonya keeps that list under Studio setup.')}
              </p>
            )}

            {choices.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {choices.map((d) => (
                  <button
                    key={d.studioDateId}
                    type="button"
                    aria-pressed={d.studioDateId === anchorId}
                    onClick={() => {
                      // Tapping another replaces the first; a chip means "due
                      // on that date" until she says otherwise, because every
                      // other default is a guess that is wrong for half the
                      // list.
                      setAnchorId(d.studioDateId)
                      setDaysBefore(0)
                    }}
                    className={`rounded-full border px-2.5 py-1.5 text-[12px] font-semibold ${
                      d.studioDateId === anchorId
                        ? 'border-accent bg-accent text-on-accent'
                        : 'border-line-2 text-ink-2 hover:border-accent hover:text-accent-2'
                    }`}
                  >
                    {d.name} &middot; {shortDate(d.on)}
                  </button>
                ))}
              </div>
            )}

            {anchor && (
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 text-[12.5px] font-semibold text-ink-2">
                  {text(content, 'asks.task.daysBefore', 'Days before')}
                  <input
                    type="number"
                    min={0}
                    max={365}
                    value={daysBefore}
                    onChange={(e) => setDaysBefore(Math.max(0, Math.min(365, Number(e.target.value) || 0)))}
                    className="w-20 rounded-lg border border-line-2 bg-surface px-2 py-1.5 text-[13px] font-normal text-ink"
                  />
                </label>

                <span className="text-[12.5px] text-ink-3">
                  {fill(
                    content,
                    'asks.task.anchoredDue',
                    { when: formatFullDate(anchoredOn ?? anchor.on), anchor: anchor.name },
                    'Due {{when}}, for {{anchor}}',
                  )}
                </span>

                <button
                  type="button"
                  onClick={clearAnchor}
                  className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
                >
                  {text(content, 'asks.task.typeInstead', 'Type a date instead')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Whether the assignee is reminded when it falls due (D-554). */}
        <label className="flex items-center gap-2 text-[12.5px] text-ink-2">
          <input
            type="checkbox"
            checked={nudges}
            onChange={(e) => setNudges(e.target.checked)}
            className="h-4 w-4 accent-accent"
          />
          {text(content, 'asks.nudges.switch', 'Remind them if they have not answered')}
        </label>

        {assign.error && (
          <p role="alert" className="text-[12.5px] text-crit">
            {(assign.error as Error).message}
          </p>
        )}

        {staff.isError && (
          <p role="alert" className="text-[12.5px] text-crit">
            {text(content, 'asks.task.staffFailed', 'Could not load who this can be for. Please try again.')}
          </p>
        )}

        <div className="mt-1 flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={!ready}
            className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-50"
          >
            {text(content, 'asks.task.send', 'Assign')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
          >
            {text(content, 'asks.task.cancel', 'Never mind')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
